import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n, ll, friendlyValidity} from "../../i18n/I18n";
import * as array from "../../utils/array";
import {Timer} from "../../utils/Timer";
import {isWebglEnabled} from "../../utils/webgl";
import {icons} from "../../icons/Icons";
import {Rect} from "../../utils/Rect";

import {Page} from "../../components/flipp-page/Page";
import {Dialog, DialogHeader} from "../../components/flipp-dialog/Dialog";
import {Link} from "../../components/flipp-link/Link";
import {LazyImage} from "../../components/flipp-lazy-image/LazyImage";
import {FlyerViewElement} from "../../components/flipp-flyerview/FlyerViewElement";

import {inject} from "../../services/di";
import {LocationService, PostalCode} from "../../services/LocationService";
import {SeoService} from "../../services/SeoService";
import {FlyerService} from "../../services/FlyerService";
import {UserAgentService} from "../../services/UserAgentService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {FavouriteService} from "../../services/FavouriteService";
import {EVTrackingService} from "../../services/EVTrackingService";
import {BlacklistService} from "../../services/BlacklistService";
import {LinkService} from "../../services/LinkService";
import {ConfigService, RuntimeConfig} from "../../services/ConfigService";
import {FavouriteContext, CrossBrowseAction, AggregateSourceAction, SourceAction} from "../../services/definitions/IAnalyticsService";
import {Backflipp} from "../../services/BackflippService";

import {Flyer, FlyerDetails} from "../../definitions/FlyerDefinitions";
import {DisplayType} from "../../definitions/BackflippDefinitions";
import {isCrawler} from "../../utils/crawler";
import MetadataService from "../../services/MetadataService";
import { ExpiredCanonicalsService } from "../../services/ExpiredCanonicalsService";
import { ExpiredMetadaService } from "../../services/ExpiredMetadaService";
import {HistoryService} from "../../services/HistoryService";
import { BannerService } from "../../services/BannerService";
import { AuthenticationService } from "../../services/AuthenticationService";
import { AccountsUser } from "../../definitions/CommonDefinitions";
import { CrossBrowseFlyerRunData, CrossBrowseFlyerRunService } from "../../services/CrossBrowseFlyerRunService";
import { AppsFlyerService } from "../../services/AppsFlyerService";


/**
 * The amount of time to wait (in ms), before firing an engagement and view event.
 */
const ENGAGEMENT_TIMEOUT = 6000;
const VIEW_TIMEOUT = 500;

interface FlyerPageAttributes extends JSX.HTMLProps {
	flyerId: number;
	flyerRunId: number;
	sourceAction: AggregateSourceAction;
	itemId?: number;
	flyerDetails?: FlyerDetails;
	h1?: string;
	merchant?: string;
}

type FlyerPageModel = [Flyer, FlyerDetails, Flyer, Flyer[], Rect[]];

@element("flipp-flyer-page")
export class FlyerPage extends Component<FlyerPageAttributes, FlyerPageModel> {
	@attr flyerRunId: number;
	@attr flyerId: number;
	@attr sourceAction: AggregateSourceAction;
	@attr itemId: number;
	@attr h1: string;
	@attr merchant: string;
	@handler flyerDetails: FlyerDetails;

	@inject location: LocationService;
	@inject userAgent: UserAgentService;
	@inject flyerService: FlyerService;
	@inject favourites: FavouriteService;
	@inject analytics: AnalyticsService;
	@inject evTracker: EVTrackingService;
	@inject linkService: LinkService;
	@inject config: ConfigService;
	@inject seo: SeoService;
	@inject backflipp: Backflipp;
	@inject blacklist: BlacklistService;
	@inject metadataService: MetadataService;
	@inject expiredCanonicalsService: ExpiredCanonicalsService;
	@inject expiredMetadaService: ExpiredMetadaService;
	@inject historyService: HistoryService;
	@inject bannerService: BannerService;
	@inject crossBrowseFlyerRunService: CrossBrowseFlyerRunService;
	@inject appsflyerService: AppsFlyerService;

	state: {
		scale: number,
		bounds: {
			top: number,
			left: number,
			width: number,
			height: number
		}
	};
	refs: {
		leftButton: HTMLButtonElement,
		rightButton: HTMLButtonElement,
		zoomInButton: HTMLButtonElement,
		zoomOutButton: HTMLButtonElement,
		pageSlider: HTMLDivElement,
		pageSliderLabel: HTMLSpanElement,
		fsaInput: HTMLInputElement,
		fsaForm: HTMLFormElement,
		fsaTip: HTMLDivElement,
		container: HTMLDivElement,
		flyerView: FlyerViewElement,
		publisherName: HTMLInputElement
	};

	private _viewTimer: Timer;
	private _sliderTimeout: number;
	private _engagementTimer: Timer;
	private _sentEngagement = false;
	private _interactionCount: number; // Counter to track the number of times the user interacts (zoom/pan)
	private _crossBrowseListFlyerRunData: CrossBrowseFlyerRunData;

	onCreate() {
		this._interactionCount = 0;
		this._viewTimer = new Timer(VIEW_TIMEOUT, this._handleViewTimeout);
		if (this.analytics.isAnalyticsDelayed()) {
			/* If analytics is delayed (eg: for a native partner), don't set up the engagement
				* timer until AFTER we recieve the 'flipp-analytics' event which is our signal that
				* the foreground experience is active */
			window.addEventListener("flipp-analytics", this._handleAnalyticsEvent);
		} else {
			this._setupTimers();
		}
	}

	onAttach() {
		window.addEventListener("popstate", (e) => {
			e.preventDefault();
			e.stopImmediatePropagation();
			this._handleBackOnCrossBrowse();
		});
	}

	onDetach() {
		if (this._engagementTimer) {
			this._engagementTimer.stop();
		}
	}

	prepare() {
		return Promise.all([
			this.flyerService.getFlyer(this.flyerId),
			this.flyerService.getFlyerDetails(this.flyerId),
			this.flyerService.getFlyers()
		]).then(([flyer, details, flyers]: [Flyer, FlyerDetails, Flyer[]]) => {
			if (!flyer) {
				/* This is a workaround for the case where the requested flyer is not found. We want to
				 * the ErrorPage message instead of the flyer experience */
				return [] as FlyerPageModel;
			} else {
				if (this._engagementTimer && !this.flyerDetails) {
					/* Start the engagement timer once we've fetched all the data */
					this._engagementTimer.start();
				}

				const sortedPages = this._getSortedPages(details);
				const nextFlyer = this.getNextFlyer(flyers);
				const xBrowseFlyers = this.getXBrowseFlyers(flyers);
				return [flyer, details, nextFlyer, xBrowseFlyers, sortedPages] as FlyerPageModel;
			}
		});
	}

	renderLoading() {
		const experienceContainerStyle = this.config.showPageTopBar ? {} : { marginTop: 0 };

		if (this.flyerDetails && this.flyerDetails.flyer) {
			if (this._engagementTimer) {
				this._engagementTimer.start();
			}

			const sortedPages = this._getSortedPages(this.flyerDetails);

			/* The component framework doesn't assign 'model' until after the promises in prepare are
			 * resolved. Here we manually assign it so that all subsequent event handlers (eg: view and/or
			 * engagement timer) are able to use the model directly even before prepare() resolves. This
			 * is a hack, but it's cleaner than passing the flyer/flyerDetails around */
			this.model = [this.flyerDetails.flyer, this.flyerDetails, null, null, sortedPages];
			/* Schedule the setup of event handlers on the flyer view handler. This has to be done on
			 * next tick because we need to get access to the elements via refs, and refs aren't assigned
			 * until after renderLoading() is returned. */
			setTimeout(this._setupFlyerViewListeners, 0);
			return this._doRender(true, this.model);
		} else {
			return <Page show="flyers"
									classList={{
										mobile: this.userAgent.mobile
									}}>
				<div class="topbar" flex-vertical flex-shrink></div>
				<div class="experience-container" style={experienceContainerStyle} flex-horizontal fill-absolute>
					<div class="flyer-view-container" flex-vertical flex-grow>
						<div class="loading">
							<div class="spinner"></div>
						</div>
					</div>
					<div class="xbrowse-section"></div>
				</div>
			</Page>;
		}
	}

	render(model: FlyerPageModel) {
		return this._doRender(false, model);
	}

	onRender() {
		if (this.refs.flyerView) {
			if (this.state.bounds) {
				/* Dont set the initial bounds from state if itemID is provided (view should be zoomed to item) */
				this.refs.flyerView.initialBounds = new Rect(
						this.state.bounds.left,
						this.state.bounds.top,
						this.state.bounds.width,
						this.state.bounds.height);
			}
			this._setupFlyerViewListeners();
		}

		// https://flippit.atlassian.net/browse/SEO-864
		if (this.refs.container && this.userAgent.mobile && this.bannerService.isSmartNews()) {
			const c = this.refs.container.getBoundingClientRect();
			this.refs.container.style.width = `${c.width + 5}px`;
		}

		// If this.flyerDetails is defined, then that means that we are in the Fast First Flyer
		// Distribution case i.e. FlyerDetails have been passed to FlyerPage and we are able to
		// render the CURRENT flyer without waiting on the rest of the flyers data to come back.
		// As a result, the engagementTimer has already started inside renderLoading() in that case
		// and we don't need to reset it here.
		// ----------------------------------------------------------------------------------------
		if (this.attached && this._engagementTimer && this.model.length > 0 && !this.flyerDetails) {
			this._engagementTimer.reset();
		}
	}

	getNextFlyer(flyers: Flyer[]): Flyer {
		const {flyer, crossBrowseListFlyerRunData} = this.crossBrowseFlyerRunService.getNextFlyer(flyers);
		this._crossBrowseListFlyerRunData = crossBrowseListFlyerRunData;

		if (flyer) {
			return flyer;
		}
		// get the index of the current flyer
		const current = array.findIndex(flyers, (flyer: Flyer) => {
			return this.flyerRunId === flyer.flyerRunId;
		});

		// start looking for 'next premium'
		for (let i = current + 1; i < flyers.length; i++) {
			const flyer = flyers[i];
			if (flyer.premium && this.analytics.isFreshFlyer(flyer.id)) {
				return flyer;
			}
		}

		// Look for any premium flyer higher up in the stack
		for (let i = 0; i < current; i++) {
			const flyer = flyers[i];
			if (flyer.premium && this.analytics.isFreshFlyer(flyer.id)) {
				return flyer;
			}
		}
		// No next premium, just return the next one
		return flyers[(current + 1) % flyers.length];
	}

	getXBrowseFlyers(flyers: Flyer[]): Flyer[] {
		if (this.config.xbrowseSameMerchMode) {
			const flyersById = flyers.filter(flyer => {
				return flyer.id === this.flyerId;
			});

			if (flyersById.length === 0) {
				return [];
			}

			let merchantId = flyersById[0].merchantId;
			const originalFlyerId = parseInt(this.analytics.isInitialized().originFlyerId());
			const OriginFlyerAlwaysFirst = (flyersArray: Flyer[]) => {
				if (this.flyerId === originalFlyerId) {
					return flyersArray.slice(0, 5);
				} else {
					return [...flyers.filter((item: Flyer) => item.id === originalFlyerId), ...flyersArray.slice(0, 4)];
				}
			};

			const filteredFlyers = flyers.filter((flyer: Flyer) => {
				const removeCurrentFlyer = this.flyerId !== flyer.id;
				const removeOriginFlyer = originalFlyerId !== flyer.id;
				const onlyMerchantFlyers = merchantId === flyer.merchantId;

				return removeCurrentFlyer && removeOriginFlyer && onlyMerchantFlyers;
			});

			return OriginFlyerAlwaysFirst(filteredFlyers);
		} else {
			return flyers.filter((flyer) =>
					this.flyerId !== flyer.id && !this.evTracker.hasEngaged(flyer.id, "ev"))
					.slice(0, 5);
		}
	}

	private _setupFlyerViewListeners = () => {
		if (this.refs.flyerView) {
			this.refs.flyerView.onboundschange = this._handleBoundsChange;
			this.refs.flyerView.oninteraction = this._handleInteraction;
			this.refs.flyerView.addEventListener(
					"itempop", this._handleFlyerViewItemPop);

			if (isWebglEnabled()) {
				// Call boundsChanged on next tick to force the view timer after initialization
				setTimeout(() => { this._handleBoundsChange(); }, 0);
			}
		}
	}

	private _getSortedPages(flyerDetails: FlyerDetails) {
		const sortedPages = flyerDetails.pages.map((p) =>
				new Rect(p.left, -p.top, p.right - p.left, p.top - p.bottom)
			).sort((lhs, rhs) => lhs.left <= rhs.left ? -1 : 1);

		return sortedPages;
	}

	private getH1() {
		return (isCrawler() && this.props.h1) ? <h1>{this.props.h1}</h1> : null;
	}

	private renderExpiredPage() {
		const link = this.expiredCanonicalsService.getFlyersLink();
		this.metadataService.setCanonicalAsync(link);
		if (isCrawler()) {
			const body = this.expiredMetadaService.getFlyerBody();
			const h1 = this.expiredMetadaService.getFlyerH1();
			return <Page show="flyers" classList={{ mobile: this.userAgent.mobile }}>
				<div>
					<h1>{h1}</h1>
					<div>{body}</div>
				</div>
			</Page>;
		}

		this.metadataService.redirectAsync(link, "redirect_flyer_message");
		return null;
	}

	private _doRender(loading: boolean, model: FlyerPageModel) {
		if (model.length === 0 && !this.flyerDetails) {
			return this.renderExpiredPage();
		}

		let [flyer, details, nextFlyer, xBrowseFlyers] = model;

		if (!flyer && details && details.flyer) {
			// Fast Flyer Case.
			// This is a case where, in DISTRIBUTION, we currently have just the
			// flyer details available for ONE particular flyer. The info for any
			// other flyers did not come back yet. For now, render whatever we can
			// render. Everything else will show a spinner.
			// ----------------------------------------------------------------------
			flyer = details.flyer;
		}

		const zoomIn = i18n("zoom_in");
		const zoomOut = i18n("zoom_out");
		const previousPage = i18n("previous_page");
		const nextPage = i18n("next_page");
		const correctionNotice = i18n("correction_notice_button");
		const nextFlyerLabel = i18n("next_flyer");

		const validity = i18n("valid_from_to", {
			from: ll(flyer.validFrom),
			to: ll(flyer.validTo)
		});

		const storeInfo = i18n("store_info");
		const showStoreInfo = flyer.isStoreSelect;
		const analyticParams = this.analytics.analyticsParameters;
		const buttonStyle = {backgroundColor: this.config.getThemeColor()};

		const experienceContainerStyle = this.config.showPageTopBar ? {} : { marginTop: 0 };

		this.seo.insertLoadingMarker(70);
		return <Page show="flyers"
								merchant={this.merchant}
								classList={{
									mobile: this.userAgent.mobile
								}}>
			<div flex-vertical fill-absolute>
				{this.getH1()}
				{ this.config.showPageTopBar ?
					<div class="topbar" flex-vertical flex-shrink>
						<div flex-horizontal>
							<div class="listing-link" flex-horizontal flex-shrink>
								<Link back to="listing" handler={this._handleBackOnCrossBrowse}>{icons.backArrow}</Link>
							</div>
							{this.flyerDetails === null ?
								<FlyerFavouriteToolbarButton flyerId={flyer.id}/>
								:
								null
							}
							<div class="title" flex-horizontal flex-grow>
								<h1 flex-horizontal>
									<LazyImage class="logo" alt="" fit contain
										dataSrc={flyer.merchantLogo}/>
									<span class="subtitle">{flyer.merchant}</span>
								</h1>
								<span class="validity">{validity}</span>
							</div>
							<div class="info-buttons" flex-horizontal flex-shrink>
								<FlyerFavouriteToolbarButton flyerId={flyer.id}/>
								{details.hasCorrections ?
									<button class="correction-notice"
											title={correctionNotice}
											aria-label={correctionNotice}
											onClick={this._showCorrectionNotices}>
										{icons.warning}
									</button>
									: null}

								{showStoreInfo ?
									<button class="store-info"
											title={storeInfo}
											aria-label={storeInfo}
											onClick={this._showStoreInfo}>
										{icons.info}
									</button>
									: null}
							</div>

							<form class="fsa-form"
								ref="fsaForm"
								action={this.linkService.linkTo("distribution")}
								onSubmit={this._handleFsaSubmit}
								flex-horizontal>
								<div>{i18n("my_location_title")}</div>
								<div class="fsa-tip" ref="fsaTip">{i18n("postal_code_tip")}</div>
								{Object.getOwnPropertyNames(analyticParams).map((prop) =>
									<input type="hidden" name={prop} value={analyticParams[prop]}/>
								)}
								<input type="hidden" name="flyer_id" value={`${flyer.id}`}/>
								<input type="hidden" name="locale" value={this.location.locale}/>
								<input type="hidden" name="location_source" value="1"/> {/* 1 is 'USER_SUPPLIED' */}
								<input type="hidden" name="blacklist_merchant_ids"
									value={this.blacklist.getBlacklist().join(",")}/>
								<input type="hidden" name="suppressed_app_top_bar" value={this.config.suppressedAppTopBar.toString()} />
								<input type="hidden" name="show_all_flyers_button" value={this.config.showAllFlyersButton.toString()} />
								<input type="hidden" name="xbrowse_same_merch" value={this.config.xbrowseSameMerchMode.toString()} />
								<input type="hidden" name="aid" value={this.config.aidHero ? "hero_flyers" : ""} />
								<input type="hidden" name="module_id" value={this.config.moduleId.toString()} />
								<input type="hidden" name="campaign_id" value={this.config.campaignId.toString()} />
								<input type="hidden" name="theme_color" value={this.config.getThemeColor().split("#")[1]} />
								<img src="https://f.wishabi.net/creative/Distribution/images/location.png" />
								<input
									type="text"
									placeholder={this.location.postalCode}
									ref="fsaInput"
									name="fsa"
									onBlur={this._fsaInputBlur}
									onInput={this._fsaInput}
									maxlength="7"/>
								{this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
									Object.getOwnPropertyNames(analyticParams).map((prop) =>
										<input type="hidden" name={prop} value={analyticParams[prop]}/>
									)
									:
									null
								}
								{this.config.CDP_SIMPLE_HEADER ?
									<div>
										<input ref="publisherName" type="hidden" name="publisher_name"
											value={this.config.CDP_PUBLISHER_NAME}/>
										<input type="hidden" name="website_url" value={this.config.CDP_BACK_LINK}/>
									</div>
									:
									null
								}
							</form>

						</div>
					</div>
					:
					null
				}
				<div ref="container" class="experience-container" style={experienceContainerStyle} flex-horizontal fill-absolute>
					<div class="flyer-view-container" flex-vertical flex-grow>
						<div class="zoom-buttons">
							<button ref="zoomInButton" onClick={this._handleZoomIn}
											class="flyer-zoom-in-button" flex title={zoomIn}
											aria-label={zoomIn}
											style={buttonStyle}>{icons.zoomIn}</button>
							<hr/>
							<button ref="zoomOutButton" onClick={this._handleZoomOut}
											class="flyer-zoom-out-button" flex title={zoomOut}
											aria-label={zoomOut}
											style={buttonStyle}>{icons.zoomOut}</button>
						</div>

						<button ref="leftButton" onClick={this._handlePreviousPage}
										class="previous-page" flex title={previousPage}
										aria-label={previousPage}
										style={buttonStyle}>{icons.leftArrow}</button>
						<button ref="rightButton" onClick={this._handleNextPage}
										class="next-page" flex title={nextPage}
										aria-label={nextPage}
										style={buttonStyle}>{icons.rightArrow}</button>

						{nextFlyer
						? <Link class="next-flyer"
									to={nextFlyer}
									onClick={() => {
										this._handleCrossBrowse();
									}}
									sourceAction={SourceAction.CrossBrowse}
									style={buttonStyle}><span>{nextFlyerLabel}</span>
							{icons.downArrow}
						</Link>
						: <div class="loading next-flyer">
								<div class="spinner"></div>
							</div>}

						{/*
							It seems that there is no way to pass FlyerDetails object to flipp-flyerview
							(it seems to support passing only primitive values.) In order to
							be able to utilize the FastFlyer object that is available in
							FlyerPage component, I set the _flyerDetailsCache in FlyerPage.tsx so that
							FlyerViewElement.tsx can ACCESS IT. Ideally, we would just pass the FlyerDetails object
							directly to flipp-flyerview.

							To fix, we'd need to enable support for adding custom properties to CustomElement classes
							in Templates.ts
						*/}
						<flipp-flyerview
							ref="flyerView"
							fill-absolute
							flyerid={this.flyerId}
							itemid={this.itemId}
							fastflyer={loading && this.flyerDetails !== null}
						/>

						{isWebglEnabled() ?
							<div ref="pageSlider" class="page-slider">
							{/*
								<input ref="pageSlider"
									onInput={this._handlePageSlider}
									type ="range"
									min ="1"
									step ="1"
									value="1"
									disabled/>
								*/}
								<span class="label" ref="pageSliderLabel" flex-vertical></span>
							</div>
						:
							null
						}
					</div>
					{ !this.config.xbrowseVisible || (this.config.xbrowseSameMerchMode && (!xBrowseFlyers || xBrowseFlyers.length === 0))
						?
						null
						:
						<div class="xbrowse-section">
							<div class="header">
								<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/categoryStartFlare.svg"	alt=""/>
								<h3>{i18n("xbrowse_more_flyers")}</h3>
							</div>

							{ xBrowseFlyers
								?
								<div class="item-container" flex-vertical>
									{xBrowseFlyers.map((flyer, idx) => {
										return <Link class="xbrowse-flyer-link" to={flyer}
												sourceAction={SourceAction.CrossBrowse}
												onClick={this._handleFlyerSuggestion.bind(this, flyer, idx)}>
											<LazyImage class="flyer-thumbnail" alt="" fit cover
												dataSrc={flyer.thumbnail}/>
											<div class="logo-container" flex-horizontal>
												<LazyImage class="logo" alt="" fit contain
													dataSrc={flyer.merchantLogo}/>
												<div flex-vertical flex-grow>
													<p class="merchant-name">{flyer.merchant}</p>
													<p>{friendlyValidity(flyer.validFrom, flyer.validTo)}</p>
												</div>
											</div>
										</Link>;
									})}
									<span class="spacer"></span>
									{this.config.showAllFlyersButton ?
										<Link class="see-all" to="listing" onClick={this._handleSeeAllFlyers}
											flex-horizontal
											style={buttonStyle}>
											<span>{i18n("xbrowse_see_all_flyers")}</span>
											{icons.rightArrow}
										</Link>
										:
										null
									}
								</div>
								: <div class="loading">
									<div class="spinner"></div>
								</div>}
						</div>
					}
				</div>
			</div>
		</Page>;
	}

	private _handleAnalyticsEvent = (evt: CustomEvent) => {
		this._setupTimers();
	}

	private _setupTimers = () => {
		this._engagementTimer = new Timer(ENGAGEMENT_TIMEOUT,
				this._sendEngagementEvent);
		if (this.attached) {
			this._engagementTimer.start();
		}
	}

	private _handleViewTimeout = () => {
		const [flyer] = this.model;

		this._interactionCount++;
		this.analytics.trackFlyerView(flyer,
				this.state.bounds.left,
				this.state.bounds.top,
				this.state.bounds.width,
				this.state.bounds.height,
				this.state.scale,
				this._interactionCount);
		this.appsflyerService.trackFlyerView();
	}

	private _handleInteraction = () => {
		if (this._engagementTimer) {
			this._engagementTimer.fire();
		}
	}

	private _sliderTicks = () => {
		/* Calculate the number of slider ticks. Calculate the number of
			* pages visible to the user (completely or partially) and we then
			* we know the number of remaining pages. */
		const [, , , , sortedPages] = this.model;
		const controller = this.refs.flyerView.controller;
		const visibleWidth = controller.viewportWidth / controller.scale;

		const trailing = sortedPages.filter((page) => page.left > visibleWidth ||
			page.left < visibleWidth && page.left + page.width > visibleWidth);

		// sortedPages.forEach((page, idx) => {
		// 	if (page.left < visibleWidth) {
		// 		if (page.left + page.width > visibleWidth) {
		// 			console.log(`page ${idx + 1} PARTIALLY in view`);
		// 		} else {
		// 			console.log(`page ${idx + 1} in view`);
		// 		}
		// 	}
		// 	if (page.left > visibleWidth) {
		// 		console.log(`page ${idx + 1} NOT in view`);
		// 	}
		// });
		return trailing.length + 1;
	}

	private _handleBoundsChange = () => {
		const controller = this.refs.flyerView.controller;
		if (!controller) {
			return;
		}
		if (this.refs.pageSlider && this.model.length > 0) {
			const [, , , , sortedPages] = this.model;
			const pages = sortedPages.filter((page) => page.left < controller.scrollLeft);
			const ticks = this._sliderTicks();
			let pageValue = pages.length + 1;
			if (pages.length + 1 > ticks) {
				pageValue = ticks;
			}
			this._setSliderThumb(pageValue);
			// this.refs.pageSlider.value = `${pageValue}`;
			// this.refs.pageSlider.max = `${ticks}`;
		}

		this._viewTimer.reset();

		if (controller.scrollLeft <= 0) {
			if (document.activeElement === this.refs.leftButton)
				this.refs.rightButton.focus();
			this.refs.leftButton.disabled = true;
		} else {
			this.refs.leftButton.disabled = false;
		}
		if (controller.scrollLeft >=
				(controller.scrollWidth - controller.viewportWidth /
				controller.scale)) {
			if (document.activeElement === this.refs.rightButton)
				this.refs.leftButton.focus();
			this.refs.rightButton.disabled = true;
		} else {
			this.refs.rightButton.disabled = false;
		}

		if (controller.scale >= controller.maximumScale) {
			this.refs.zoomOutButton.disabled = false;
			if (document.activeElement === this.refs.zoomInButton)
				this.refs.zoomOutButton.focus();
			this.refs.zoomInButton.disabled = true;
		} else if (controller.scale <= controller.minimumScale) {
			this.refs.zoomInButton.disabled = false;
			if (document.activeElement === this.refs.zoomOutButton)
				this.refs.zoomInButton.focus();
			this.refs.zoomOutButton.disabled = true;
		} else {
			this.refs.zoomInButton.disabled = false;
			this.refs.zoomOutButton.disabled = false;
		}

		this.state.scale = controller.scale;
		this.state.bounds = {
			top: controller.scrollTop,
			left: controller.scrollLeft,
			width: controller.viewportWidth / controller.scale,
			height: controller.viewportHeight / controller.scale
		};
	}

	private createLink = (item: any) => {
		const inst = LinkService.getInstance();
		const href = inst.linkTo(item);
		if (href) {
			inst.execute(href, this);
		}
	}

	private _handleFlyerViewItemPop = async (e: CustomEvent) => {
		const [, {items}] = this.model;

		const item = array.find(items, (item) =>
				item.id.toString() === e.detail.toString());
		if (!item) return;

		this._handleInteraction();

		switch (item.displayType) {
			case DisplayType.PAGELINK:
				this.refs.flyerView.goToPage(item.pageDestination - 1);
				break;
			case DisplayType.ITEMCORRECTION:
				this._showCorrectionNotices();
				break;
			case DisplayType.LINK:
			case DisplayType.IFRAME:
				const {ttmUrl} = await this.flyerService.getFlyerItem(item.id);
				if (!ttmUrl) {
					break;
				}
			case DisplayType.HIDEINFO:
			case DisplayType.CUSTOMLIST:
			case DisplayType.SHARETWITTER:
			case DisplayType.ITEMSIDELIST:
			case DisplayType.ITEMSIDEGRID:
			case DisplayType.TOMBSTONE:
			default:
				this.createLink(item);
				break;
		}
	}

	private _handleZoomOut = () => {
		this.refs.flyerView.scaleDown();
		this._handleInteraction();
	}

	private _handleZoomIn = () => {
		this.refs.flyerView.scaleUp();
		this._handleInteraction();
	}

	private _handlePreviousPage = () => {
		this.refs.flyerView.pageLeft();
		this._handleInteraction();
	}

	private _handleNextPage = () => {
		this.refs.flyerView.pageRight();
		this._handleInteraction();
	}

	private _setSliderThumb = (val: number) => {
		const ticks = this._sliderTicks();
		this.refs.pageSliderLabel.textContent = i18n("page_thumb", {
			start: `${val}`,
			end: `${ticks}`
		});

		const percentage = (val - 1) / (Number(ticks) - 1);
		/* Set the 'left' value for the slider label. This is just the slider value divided by the
			* width (as a percentage) minus a small value which represents the label offset. The '120px'
			* value below corresponds to the size of the slider thumb (as defined in the
			* $page-slider-thumb-width SASS variable) */
		this.refs.pageSliderLabel.style.left = `calc(${percentage * 100}% - (120px * ${percentage}))`;
		this.refs.pageSliderLabel.style.backgroundColor = this.config.getThemeColor();

		if (!this.refs.pageSlider.classList.contains("show")) {
			this.refs.pageSlider.classList.add("show");
		}
		if (this._sliderTimeout) {
			clearTimeout(this._sliderTimeout);
			this._sliderTimeout = -1;
		}
		this._sliderTimeout = setTimeout(() => {
			this.refs.pageSlider.classList.remove("show");
			clearTimeout(this._sliderTimeout);
			this._sliderTimeout = -1;
		}, 500);
	}

	private _sendEngagementEvent = () => {
		if (!this._sentEngagement) {
			this._sentEngagement = true;

			const [flyer] = this.model;
			this.analytics.trackFlyerEngagement(flyer, this.sourceAction);
			this.appsflyerService.trackFlyerEngagement(flyer);
		}
	}

	private _handleCrossBrowse = () => {
		const [flyer, , nextFlyer] = this.model;
		if (this._crossBrowseListFlyerRunData && nextFlyer) {
			this.crossBrowseFlyerRunService.handleCrossBrowse(nextFlyer.flyerRunId);
		}
		this.analytics.trackCrossBrowse(CrossBrowseAction.NextFlyer, flyer, nextFlyer);
	}

	private _handleBackOnCrossBrowse = () => {
		if (this._crossBrowseListFlyerRunData) {
			this.crossBrowseFlyerRunService.handleCrossBrowseBack();
		}
	}

	private _handleSeeAllFlyers = () => {
		this.analytics.trackCrossBrowse(CrossBrowseAction.SeeAllFlyers);
	}

	private _handleFlyerSuggestion = (toFlyer: Flyer, idx: number) => {
		const [flyer] = this.model;
		this.analytics.trackCrossBrowse(CrossBrowseAction.FlyerSuggestion, flyer, toFlyer, idx + 1);
	}

	// private _handlePageSlider = () => {
	// 	const sliderNum = Number(this.refs.pageSlider.value) - 1;
	// 	const [, , , , sortedPages] = this.model;
	// 	const pageOffset = sortedPages.length - this._sliderTicks();
	// 	let nextPageNum = sliderNum + pageOffset;
	// 	if (sliderNum === 0) {
	// 		nextPageNum = 0;
	// 	}

	// 	this._flyerView.goToPage(nextPageNum);
	// 	this._setSliderThumb(sliderNum);

	// 	this._handleInteraction();
	// 	this._sendEngagementEvent();
	// }

	private _showStoreInfo = () => {
		this._handleInteraction();

		this.backflipp.getStoreInfo(this.location.postalCode, this.flyerId.toString())
				.then((json) => {
					const dialog = (<Dialog class="store-info-dialog popover" transient>
						<DialogHeader>
							<h1 content-slot="title">
								{i18n("stores_closest_to_postal_code", {
									postalCode: this.location.postalCode
								})}
							</h1>
						</DialogHeader>

						<div class="inner">
							{ json.info.select_stores.map((store) =>
								<div class="address">
									<div>{store.name}</div>
									<div>{store.address}</div>
									<div>{store.city}, {store.province}</div>
									<div>{store.postal_code}</div>
									<div><a target="_blank" href={`http://maps.google.com/?q=${store.address}, ${store.city}, ${store.province}, ${store.postal_code}`}>{i18n("map")}</a></div>
								</div>
							)}
						</div>
					</Dialog> as any) as Dialog;

					dialog.showModal();
				});
	}

	private _showCorrectionNotices = () => {
		this._handleInteraction();

		const [flyer] = this.model;
		const dialog = (<Dialog class="correction-notice-dialog popover" transient>
			<DialogHeader>
				<h1 content-slot="title">{i18n("correction_notice")}</h1>
			</DialogHeader>

			<iframe src={this.backflipp.getFlyerCorrectionNoticesURL(flyer.id.toString())} />
		</Dialog> as any) as Dialog;

		dialog.showModal();
	}

	private _handleFsaSubmit = async (e: Event) => {
		e.preventDefault();
		this.refs.fsaForm.classList.add("invalid");

		const value = this._getFsaValue();
		if (!PostalCode.isValid(value)) {
			return;
		}

		if (this.location.postalCode === value) {
			return;
		}

		if (this.config.aidHero) {
			// if postal code doesn't exist show tip notification only for hero module
			const isPostalCode = await this.backflipp.getFlyer(this.flyerId.toString(), value, true);

			if (isPostalCode === null) {
				this.refs.fsaTip.classList.add("show");

				return;
			}
		}

		const currentCode = new PostalCode(this.location.postalCode);
		const newCode = new PostalCode(value);
		if (currentCode.country !== newCode.country) {
			return;
		}

		// On form submit, spaces are encoded as '+' characters which breaks us
		// Manually clean up the value by stripping any whitespace
		const input = this.refs.fsaInput;
		input.value = value.replace(" ", "");

		if (this.config.CDP_SIMPLE_HEADER) {
			// Manually clean publisher name values since form will replace spaces with '+'
			const publisherNameVal = this.refs.publisherName.value;
			this.refs.publisherName.value = publisherNameVal.replace(/\s{1}/g, "__");
		}

		this.refs.fsaForm.submit();
	}

	private _getFsaValue() {
		const input = this.refs.fsaInput;
		let value = input.value;
		if (value === "") {
			value = this.location.postalCode;
		}
		return value.replace(" ", "");
	}

	private _fsaInputBlur = (e: Event) => {
		e.preventDefault();

		const value = this._getFsaValue();
		if (!PostalCode.isValid(value)) {
			this.refs.fsaForm.classList.add("invalid");
		} else {
			this.refs.fsaForm.classList.remove("invalid");
			this.refs.fsaTip.classList.remove("show");
		}
	}

	private _fsaInput = (e: Event) => {
		e.preventDefault();

		const value = this._getFsaValue();
		if (PostalCode.isValid(value)) {
			this.refs.fsaForm.classList.remove("invalid");
			this.refs.fsaTip.classList.remove("show");
		}
	}
}

@element("flipp-flyer-favourite-toolbar-button")
class FlyerFavouriteToolbarButton extends Component<{flyerId: number}, Flyer> {

	@inject flyerService: FlyerService;
	@inject favourites: FavouriteService;
	@inject analytics: AnalyticsService;
	@inject auth: AuthenticationService;
	@inject appsFlyerService: AppsFlyerService;

	@attr flyerId: number;

	private isFavourite: boolean = false;
	private token: AccountsUser;

	onAttach() {
		window.addEventListener("flipp-favourites", this._handleFavouritesChanged);
	}

	onDetach() {
		window.removeEventListener("flipp-favourites", this._handleFavouritesChanged);
	}

	async prepare() {
		const flyer = await this.flyerService.getFlyer(this.flyerId);
		this.token = this.auth.user ? this.auth.user.token : null;
		this.isFavourite = await this.favourites.isFavourite(flyer, this.token);
		return flyer;
	}

	render(flyer: Flyer) {
		return <div class="favourites">
			<button onClick={this._toggleFavourite}>
				<div flex-horizontal>
					{this.isFavourite
						?
						<div class="animated-button_clicked">
							<img class="full-heart" src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-fill.svg" alt=""/>
						</div>
						:
						<div>
							<img class="empty-heart" src="https://f.wishabi.net/creative/Distribution/images/icons/favourite-outline.svg" alt=""/>
						</div>
					}
				</div>
			</button>
		</div>;
	}

	private _toggleFavourite = () => {
		if (this.isFavourite) {
			this.favourites.removeFavourite(this.model, this.token);
		} else {
			this.favourites.addFavourite(this.model, this.token);
		}

		this.analytics.trackFavouriteEvent(this.model.merchantId,
			this.isFavourite ? "Favorited" : "Unfavorited",
			FavouriteContext.FlyerView);
		if (!this.isFavourite)
			this.appsFlyerService.trackAddFavouriteEvent();
	}

	private _handleFavouritesChanged = () => {
		this.invalidate();
	}
}
